import { useCallback, useState } from 'react';
import fleetApi from '../../services/api.service';

/**
 * Hook custom pour replanifier des commandes vers une autre date
 * 
 * Met à jour la delivery_date pour les commandes non affectées
 * 
 * @returns {Object} - { rescheduleSteps, isRescheduling, error }
 */
const useRescheduleSteps = () => {
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Replanifie des commandes vers une autre date
   * @param {Object} params - { tourPlanningIds, newTourDate }
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  const rescheduleSteps = useCallback(async ({ tourPlanningIds, newTourDate }) => {
    setIsRescheduling(true);
    setError(null);

    try {
      if (!tourPlanningIds || tourPlanningIds.length === 0) {
        throw new Error('Aucune commande sélectionnée');
      }

      if (!newTourDate) {
        throw new Error('Une date de replanification est requise');
      }

      const response = await fleetApi.post(
        '/tours-planning/steps/reschedule',
        {
          tour_planning_ids: tourPlanningIds,
          new_tour_date: newTourDate,
        }
      );

      if (response.status === 200 && response.data?.success) {
        return {
          success: true,
          message: response.data?.message || 'Commandes replanifiées avec succès',
          data: response.data?.data,
        };
      }

      throw new Error('Réponse invalide du serveur');
    } catch (err) {
      const message = 
        err?.response?.data?.message || 
        err.message || 
        'Impossible de replanifier les commandes';
      setError(message);
      throw new Error(message);
    } finally {
      setIsRescheduling(false);
    }
  }, []);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    rescheduleSteps,
    isRescheduling,
    error,
    resetError,
  };
};

export default useRescheduleSteps;

